import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { BaseModalWindow } from "./BaseModalWindow";
import { GtinInfoDto } from "../../@types/kiz";

interface ICheckKizResultsModalWindowProps {
    results: GtinInfoDto[];
    onRemove: (invalidSgtins: string[]) => void;
    onIgnore: () => void;
    isRemoving?: boolean;
}

export const CheckKizResultsModalWindow: FC<ICheckKizResultsModalWindowProps> = (props) => {
    const { t } = useTranslation();
    
    if (!props.results || !Array.isArray(props.results) || props.results.length === 0) {
        return null;
    }

    const invalidItems = props.results.filter(r => r.state !== "Ok");
    const validItems = props.results.filter(r => r.state === "Ok");

    const getResultText = (state: string): string => {
        switch (state) {
            case "ErrorDateExpired": return t("modals.checkKizResults.expiredDate");
            case "ErrorBranchId": return t("modals.checkKizResults.wrongBranch");
            case "ErrorInCirculation": return t("modals.checkKizResults.wrongStatus");
            case "ErrorNotFound": return t("modals.checkKizResults.notFound");
            case "ErrorInSscc": return t("modals.checkKizResults.inBox");
            case "ErrorSscc": return t("modals.checkKizResults.inBox");
            default: return t("modals.checkKizResults.okDescription");
        }
    };

    const getDescription = (item: GtinInfoDto): string => {
        switch (item.state) {
            case "ErrorDateExpired":
                return `${t("modals.checkKizResults.expiredDate")}: ${item.dateExpired || ""}`;
            case "ErrorInSscc":
            case "ErrorSscc":
                return `${t("modals.checkKizResults.inBox")}: ${item.sscc || ""}`;
            case "ErrorInCirculation":
                return `${t("modals.checkKizResults.wrongStatus")}: ${item.status || ""}`;
            default:
                return getResultText(item.state);
        }
    };

    const handleRemove = () => {
        console.log("handleRemove called, invalidItems:", invalidItems);
        const invalidSgtins = invalidItems.map(r => r.gtin);
        props.onRemove(invalidSgtins);
    };

    const handleIgnore = () => {
        props.onIgnore();
    };

    // Если нет невалидных КИЗов
    if (invalidItems.length === 0) {
        return (
            <BaseModalWindow
                header={t("modals.checkKizResults.header")}
                ok={{
                    onClick: handleIgnore,
                    title: t("modals.checkKizResults.okButton")
                }}
                cancel={{
                    onClick: handleIgnore,
                    title: t("modals.checkKizResults.okButton")
                }}
            />
        );
    }

    return (
        <BaseModalWindow
            header={t("modals.checkKizResults.header")}
            ok={{
                onClick: handleRemove,
                title: t("modals.checkKizResults.removeButton"),
                disabled: props.isRemoving
            }}
            cancel={{
                onClick: handleIgnore,
                title: t("modals.checkKizResults.ignoreButton")
            }}
        >
            <div style={{ marginBottom: '16px', display: 'flex', gap: '20px', paddingBottom: '12px', borderBottom: '1px solid #e0e0e0' }}>
                <span>{t("modals.checkKizResults.totalCount")}: {props.results.length}</span>
                <span style={{ color: '#c62828' }}>{t("modals.checkKizResults.errorCount")}: {invalidItems.length}</span>
                <span style={{ color: '#2e7d32' }}>{t("modals.checkKizResults.okCount")}: {validItems.length}</span>
            </div>

            <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                        <tr>
                            <th>SGTIN</th>
                            <th>{t("modals.checkKizResults.result")}</th>
                            <th>{t("modals.checkKizResults.description")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {invalidItems.map((result, idx) => (
                            <tr key={idx} style={{ backgroundColor: '#ffebee' }}>
                                <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                                    {result.gtin}
                                </td>
                                <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                                    {getResultText(result.state)}
                                </td>
                                <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                                    {getDescription(result)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </BaseModalWindow>
    );
};